import React, { SyntheticEvent, useRef } from "react";
import ContentComponent from "../../components/content/ContentComponent";
import {
  Autocomplete,
  CircularProgress,
  DialogContent,
  Grid,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "@mui/lab";
import DialogActions from "@mui/material/DialogActions";
import { requestApi } from "../../helpers/RequestApi";
import { GET, POST } from "../../utils/MethodUtils";
import { EDIT_USER_URL, SIRET_URL } from "../../utils/UrlsUtils";
import { toastr } from "react-redux-toastr";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";
import { setUser } from "../../app/globalSlice";
import Divider from "@mui/material/Divider";
import getErrorApi from "../../helpers/GetErrorApi";
import Box from "@mui/material/Box";
import { SiretResponseInterface } from "../../interfaces/SiretInterface";
import siretValidator from "../../helpers/validator/SiretValidator";
import { InputInterface } from "../../interfaces/InputInterface";

interface State {
  close: any;
}

interface FormState {
  type: InputInterface;
  ctSiret: InputInterface;
  validSiret: InputInterface;
}

const SiretFormComponent: React.FC<State> = React.memo(({ close }) => {
  const token = useAppSelector((state: RootState) => state.globalState.token);
  const user = useAppSelector((state: RootState) => state.globalState.user);
  const getDefaultValues = React.useCallback((): FormState => {
    return {
      type: { value: "", error: "" },
      ctSiret: { value: null, error: "" },
      validSiret: { value: false, error: "" },
    };
  }, []);
  const [values, setValues] = React.useState<FormState>(getDefaultValues());
  const [loading, setLoading] = React.useState<boolean>(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [sirets, setSirets] = React.useState<SiretResponseInterface[]>([]);
  const [searchSiret, setSearchSiret] = React.useState<string>("");
  const [isSearching, setIsSearching] = React.useState<boolean>(false);
  const refSiret: any = useRef(undefined);

  const handleSearchSiret = React.useCallback(
    (event: SyntheticEvent<Element, Event>, value: string) => {
      setSearchSiret(value.replace(" ", ""));
      setSirets([]);
    },
    []
  );

  const handleChangeAutocomplete = React.useCallback(
    (prop: keyof FormState) =>
      (event: SyntheticEvent<Element, Event>, value: any) => {
        setValues((v) => {
          return {
            ...v,
            // @ts-ignore
            [prop]: { ...v[prop], value: value, error: "" },
          };
        });
      },
    []
  );

  const searchSirets = React.useCallback(async () => {
    const siret = searchSiret.trim();
    const siretError = siretValidator(siret);
    if (siretError || siret === values.ctSiret.value?.etablissement.siret) {
      if (siretError) {
        setValues((v) => {
          return {
            ...v,
            ctSiret: { ...v.ctSiret, error: siretError },
            validSiret: { value: false, error: siretError },
          };
        });
      }
      return;
    }
    setIsSearching(true);
    const response = await requestApi({
      method: GET,
      path: SIRET_URL + "/" + siret,
      allowError: false,
      redirect401: false,
    });
    const list: NodeList = refSiret.current.querySelectorAll("input");
    if (response.statusCode === 200) {
      setValues((v) => {
        return {
          ...v,
          ctSiret: { value: response.content, error: "" },
          validSiret: { value: true, error: "" },
        };
      });
      list.forEach((el) => {
        // @ts-ignore
        el.blur();
      });
    } else {
      if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else if (response.statusCode === 404) {
        toastr.error(t("word.error"), t("sentence.invalidSiret"));
        setValues((v) => {
          return {
            ...v,
            validSiret: { value: false, error: "" },
          };
        });
      } else {
        setValues((v) => {
          const customSiretResponse: SiretResponseInterface = {
            etablissement: {
              siret: siret,
              uniteLegale: {
                denominationUniteLegale: null,
              },
              adresseEtablissement: {
                complementAdresseEtablissement: null,
                numeroVoieEtablissement: null,
                typeVoieEtablissement: null,
                libelleVoieEtablissement: null,
                codePostalEtablissement: null,
                libelleCommuneEtablissement: null,
              },
            },
          };
          return {
            ...v,
            ctSiret: {
              value: customSiretResponse,
              error: "",
            },
            validSiret: { value: false, error: "" },
          };
        });
        list.forEach((el) => {
          // @ts-ignore
          el.blur();
        });
      }
    }
    setIsSearching(false);
  }, [searchSiret, t, values.ctSiret.value?.etablissement.siret]);

  const submit = React.useCallback(async () => {
    setLoading(true);
    const response = await requestApi({
      method: POST,
      path: EDIT_USER_URL,
      allowError: false,
      body: {
        userIdentifier: user?.userIdentifier,
        ctSiret: values.ctSiret.value.etablissement.siret,
      },
      token: token,
    });
    if (response.statusCode === 200) {
      dispatch(setUser(response.content));
      toastr.success(
        t("word.success"),
        t("sentence.notification.siret_updated")
      );
    } else if (response.statusCode === 401) {
      toastr.info(t("word.info"), t("error.reconnect"));
    } else {
      for (let message of getErrorApi(response.content)) {
        toastr.error(t("word.error"), t(message));
      }
    }
    setLoading(false);
    close();
  }, [close, dispatch, t, token, user?.userIdentifier, values.ctSiret.value]);

  React.useEffect(() => {
    const timeoutTyping = setTimeout(() => {
      searchSirets();
    }, 500);
    return () => clearTimeout(timeoutTyping);
  }, [searchSirets]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <DialogContent>
        <ContentComponent
          contentName="editSiret"
          saveLocalStorage={true}
          width={320}
        />
        <Divider sx={{ marginBottom: 2 }} />
        <Grid container spacing={1} sx={{ marginBottom: 2 }}>
          <Grid item xs={12}>
            <Autocomplete
              options={sirets}
              forcePopupIcon={false}
              noOptionsText={
                isSearching ? (
                  <Box sx={{ textAlign: "center" }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  t("word.searchSiret")
                )
              }
              inputValue={searchSiret}
              onInputChange={handleSearchSiret}
              getOptionLabel={(siretResponse: SiretResponseInterface) => {
                return siretResponse.etablissement.siret;
              }}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.etablissement.siret}>
                    {option.etablissement.siret}
                  </li>
                );
              }}
              isOptionEqualToValue={(
                option: SiretResponseInterface,
                value: SiretResponseInterface
              ) => option.etablissement.siret === value.etablissement.siret}
              onChange={handleChangeAutocomplete("ctSiret")}
              value={values.ctSiret.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  ref={refSiret}
                  autoComplete="off"
                  error={!!values.ctSiret.error}
                  helperText={t(values.ctSiret.error ?? "")}
                  required={true}
                  label={t("word.siret")}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <LoadingButton onClick={close} disabled={loading}>
          {t("word.cancel")}
        </LoadingButton>
        <LoadingButton
          variant="contained"
          onClick={submit}
          loading={loading}
          disabled={!values.validSiret.value}
        >
          {t("word.save")}
        </LoadingButton>
      </DialogActions>
    </>
  );
});

export default SiretFormComponent;
